import {
  Box,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton
} from "@mui/x-data-grid";
import {
  SpotSearchQuerySortableField,
  SpotSortableOrder
} from "../generatedApi/spotSearchEngineApi";
import { useSpotSearch } from "../hooks/useSpotSearch";
import { useSpotItemsQuery } from "../hooks/useSpotItemsQuery";
import { PaginatedDataGrid } from "./PaginatedDataGrid";
import { callbackPublicPathAtom, callbackTabAtom } from "../atom";
import { useAtom } from "jotai/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

export const SpotITemPagePublicTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchConditions, updateSearchConditions } = useSpotSearch();
  const [, setCallbackPublicPath] = useAtom(callbackPublicPathAtom);
  const [, setCallbackTab] = useAtom(callbackTabAtom);
  const { data, loading } = useSpotItemsQuery({
    apiName: "spot_search_engine",
    operationId: "searchSpots"
  });

  // デバウンス付きローカル検索状態管理
  const { localState, setters, clearField } = useDebouncedSearch(
    searchConditions,
    updateSearchConditions,
    500
  );

  // スポット詳細に遷移する前に現在の状態をJotaiに保存
  const handleSpotClick = (spotId: string) => {
    setCallbackPublicPath(location.search);
    setCallbackTab("public");
    navigate(`/spots/${spotId}`);
  };

  return (
    <Stack height="100%" width="80vw" direction="row" spacing={2}>
      <Stack width={300} py={1} spacing={1} sx={{ flexShrink: 0 }}>
        <Stack spacing={1}>
          <Typography variant="body1" fontWeight="bold">
            検索
          </Typography>
        </Stack>
        <TextField
          variant="filled"
          label="スポット名"
          value={localState.nameInput}
          onChange={(e) => setters.setNameInput(e.target.value)}
        />
        <TextField
          variant="filled"
          label="読み仮名"
          value={localState.phoneticNameInput}
          onChange={(e) => setters.setPhoneticNameInput(e.target.value)}
        />
        <TextField
          variant="filled"
          label="緯度"
          value={localState.latInput}
          onChange={(e) => setters.setLatInput(e.target.value)}
          type="number"
        />
        <TextField
          variant="filled"
          label="経度"
          value={localState.lonInput}
          onChange={(e) => setters.setLonInput(e.target.value)}
          type="number"
        />
        <InputLabel id="sort-field-label">ソート名</InputLabel>
        <Select
          labelId="sort-field-label"
          label="ソート名"
          variant="filled"
          value={localState.sortFieldInput}
          onChange={(e) => setters.setSortFieldInput(e.target.value)}
          endAdornment={
            <>
              <IconButton
                sx={{ display: localState.sortFieldInput ? "" : "none" }}
                onClick={() => clearField("sortFieldInput")}
              >
                <Clear />
              </IconButton>
            </>
          }
        >
          <MenuItem value={SpotSearchQuerySortableField.TIMESTAMP_PUBLISHED}>公開日時</MenuItem>
          <MenuItem value={SpotSearchQuerySortableField.TIMESTAMP_CREATED}>作成日時</MenuItem>
          <MenuItem value={SpotSearchQuerySortableField.DISTANCE}>距離</MenuItem>
        </Select>
        <InputLabel id="sort-order-label">ソート順</InputLabel>
        <Select
          labelId="sort-order-label"
          label="ソート順"
          variant="filled"
          value={localState.sortOrderInput}
          onChange={(e) => setters.setSortOrderInput(e.target.value)}
          endAdornment={
            <>
              <IconButton
                sx={{ display: localState.sortOrderInput ? "" : "none" }}
                onClick={() => clearField("sortOrderInput")}
              >
                <Clear />
              </IconButton>
            </>
          }
        >
          <MenuItem value={SpotSortableOrder.ASC}>昇順</MenuItem>
          <MenuItem value={SpotSortableOrder.DESC}>降順</MenuItem>
        </Select>
      </Stack>
      <Box sx={{ width: "100%", height: "100%", py: 1, minWidth: 0 }}>
        <PaginatedDataGrid
          loading={loading}
          sx={{
            bgcolor: "#fff",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden"
          }}
          rows={data?.results ?? []}
          rowCount={data?.total ?? 0}
          disableColumnFilter
          pageSizeOptions={[5, 10, 20, 100]}
          columns={[
            {
              field: "spot_id",
              headerName: "スポットID",
              width: 200,
              minWidth: 200,
              flex: 0,
              renderCell: (params) => (
                <span
                  style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => handleSpotClick(params.value)}
                >
                  {params.value}
                </span>
              ),
              sortable: false
            },
            {
              field: "name",
              headerName: "スポット名",
              minWidth: 200,
              flex: 1,
              sortable: false
            },
            {
              field: "phonetic_name",
              headerName: "読み仮名",
              minWidth: 150,
              flex: 1,
              sortable: false
            },
            {
              field: "revision_id",
              headerName: "v",
              width: 80,
              minWidth: 60,
              flex: 0,
              sortable: false
            },
            {
              field: "timestamp_published",
              headerName: "公開日時",
              width: 160,
              minWidth: 120,
              flex: 0,
              sortable: false
            },
            {
              field: "timestamp_updated",
              headerName: "作成日時",
              width: 160,
              minWidth: 120,
              flex: 0,
              sortable: false
            },
            {
              field: "distance",
              headerName: "距離",
              width: 100,
              minWidth: 80,
              flex: 0,
              sortable: false,
              renderCell: (params) => {
                return (
                  <Typography variant="body2">
                    {params.value == null ? "-" : `${Math.floor(params.value * 100) / 100}km`}
                  </Typography>
                );
              }
            },
            {
              field: "location",
              headerName: "位置情報",
              width: 180,
              minWidth: 150,
              flex: 0,
              sortable: false,
              renderCell: (params) => {
                return (
                  params.row.location && (
                    <a
                      href={`https://www.google.co.jp/maps/@${params.row.location?.lat},${params.row.location?.lon},14z`}
                      target="_blank"
                    >
                      {params.row.location?.lat},{params.row.location?.lon}
                    </a>
                  )
                );
              }
            }
          ]}
          hideFooterSelectedRowCount
          getRowId={(row) => row.spot_id}
          checkboxSelection
          slots={{
            toolbar: () => (
              <GridToolbarContainer sx={{ display: "flex", justifyContent: "space-between" }}>
                <Stack direction="row">
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
                  <GridToolbarExport />
                </Stack>
                <Stack direction="row" spacing={1}></Stack>
              </GridToolbarContainer>
            )
          }}
        />
      </Box>
    </Stack>
  );
};
